import { useCallback, useEffect, useState, type ReactNode } from "react";
import { CookieConsentContext, type CookieConsentContextValue } from "../utils/cookieConsentContext";
import { readConsent, writeConsent, type CookiePreferences } from "../utils/cookieConsentStore";
import { loadGA4 } from "../utils/analytics";

export default function CookieConsentProvider({ children }: { children: ReactNode }) {
  // Lazy initializer reads localStorage synchronously before first paint —
  // no useEffect + flash-then-hide for a returning visitor who already chose.
  const [preferences, setPreferences] = useState<CookiePreferences | null>(() => readConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // Covers both a fresh accept this session and a returning visitor who
  // already consented on a prior visit (preferences is read from
  // localStorage above, so this fires on mount too) — loadGA4 is a no-op
  // once already loaded or if no real measurement ID is configured.
  useEffect(() => {
    if (preferences?.analytics) loadGA4();
  }, [preferences?.analytics]);

  const save = useCallback((prefs: CookiePreferences) => {
    writeConsent(prefs);
    setPreferences(prefs);
    setPreferencesOpen(false);
  }, []);

  const value: CookieConsentContextValue = {
    preferences,
    preferencesOpen,
    openPreferences: () => setPreferencesOpen(true),
    closePreferences: () => setPreferencesOpen(false),
    acceptAll: () => save({ essential: true, analytics: true, marketing: true }),
    rejectNonEssential: () => save({ essential: true, analytics: false, marketing: false }),
    savePreferences: save,
  };

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}
